import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type { PrototypeOptions } from '@/components/app/prototypeOptions';
import { defaultPrototypeOptions } from '@/components/app/prototypeOptions';

type PrototypeOptionsValue = {
  options: PrototypeOptions;
  setOptions: (changes: Partial<PrototypeOptions>) => void;
};

const PrototypeOptionsContext = createContext<PrototypeOptionsValue>({
  options: defaultPrototypeOptions,
  setOptions: () => {},
});

/**
 * The options panel lives in the builder, but its choices describe the whole
 * prototype — compact mode reaches the dashboard's list too. Holding them above
 * the router is what lets a choice survive moving between the two screens.
 */
export function PrototypeOptionsProvider({ children }: { children: ReactNode }) {
  const [options, setState] = useState<PrototypeOptions>(defaultPrototypeOptions);

  const value = useMemo(
    () => ({
      options,
      setOptions: (changes: Partial<PrototypeOptions>) =>
        setState(current => ({ ...current, ...changes })),
    }),
    [options],
  );

  return (
    <PrototypeOptionsContext.Provider value={value}>{children}</PrototypeOptionsContext.Provider>
  );
}

export function usePrototypeOptions() {
  return useContext(PrototypeOptionsContext);
}
